import { useSyncExternalStore } from 'react';
import { STREAM_API, API_UNREACHABLE, API_REACHABLE } from './streamApi';

// One app-wide "is the server reachable?" flag, fed by apiFetch's network
// events (see streamApi.js). navigator.onLine can't tell Wi-Fi-with-no-uplink
// apart from a working connection, so failed requests are the real signal.
// Shared by the offline banner and the Local Mode nav entry so they always
// agree.

// A single failed request can be a blip; two in a row is an outage.
const FAILS_BEFORE_UNREACHABLE = 2;

let _unreachable = typeof navigator !== 'undefined' && navigator.onLine === false;
let _fails = 0;
const _listeners = new Set();

function _set(v) {
  if (v === _unreachable) return;
  _unreachable = v;
  _listeners.forEach(fn => fn());
}

if (typeof window !== 'undefined') {
  window.addEventListener(API_UNREACHABLE, () => { if (++_fails >= FAILS_BEFORE_UNREACHABLE) _set(true); });
  window.addEventListener(API_REACHABLE,   () => { _fails = 0; _set(false); });
  window.addEventListener('offline',       () => _set(true));
}

export function isServerUnreachable() { return _unreachable; }

const PROBE_TIMEOUT_MS = 6000;

// One-off check for pages that can open before any API call has run (Local
// Mode, reached from the login page). Any HTTP response — even a 404/401 —
// means the server answered; only a network failure or timeout counts as
// unreachable. A deliberate probe, so one failure is enough.
export async function probeServer() {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), PROBE_TIMEOUT_MS);
  try {
    await fetch(`${STREAM_API}/`, { cache: 'no-store', signal: ctrl.signal });
    _fails = 0;
    _set(false);
  } catch {
    _set(true);
  } finally {
    clearTimeout(t);
  }
}

function _subscribe(cb) { _listeners.add(cb); return () => _listeners.delete(cb); }

export function useServerUnreachable() {
  return useSyncExternalStore(_subscribe, isServerUnreachable, isServerUnreachable);
}
